import React, {useContext, useEffect, useState} from 'react';
import {Accordion, TabInput, Toggle} from '../../../../components';
import {useViewerStore} from "../../../../store/store";
import {useCurrentUser} from "../../../../contexts/UserContext";
import {FOOT, INCH, METER, ROOF, UnitContext} from '../../../../contexts/UnitContext';
import {Button, Divider, Flex} from 'antd';
import {ArrowsOutCardinal, Eraser, Question, XSquare} from "@phosphor-icons/react";
import {MdOutlineCropLandscape, MdOutlineCropPortrait} from "react-icons/md";
import {BsGrid1X2} from "react-icons/bs";
import SliderInput from "../../../../components/Pure-UI-Components/SliderInput/SliderInput";
import {GetRoundNum} from "../../../../components/Layout-Components/Editor-Layout/Three-JS-Viewer/Controllers/Common";
import {SLIDER_PROPERTIES_SETTINGS} from "../../../../Constants";
import { RoofMesh } from '../../../../@types/types';
import { MeshType, ModuleOrientation, EditAction } from '../../../../@types/enums';
import {RiBuildingFill} from "react-icons/ri";
import {
    setBuildingAsDetachedStructure
} from "../../../../components/Layout-Components/Editor-Layout/Three-JS-Viewer/Controllers/ModuleControl";

const RoofProperties: React.FC = () => {

    const sceneViewer = useViewerStore(state => state.sceneViewer);

    const {user, hasCapability} = useCurrentUser();
    const {roofUnit, convertTypeQuantityToUnit, convertQuantityToUnit, renderUnit} = useContext(UnitContext);

    const unitKeyMap: Record<string, 'INCH' | 'FOOT' | 'METER'> = {
        ft: 'FOOT',
        in: 'INCH',
        m: 'METER',
    };
    const mappedUnit = unitKeyMap[roofUnit]; // or default to 'METER'

    const { wallEditedHeight, wallInitialHeight, isRoofTagVisible, isMoveRoofActive,
        selRoofFaceArr, refreshQuestionableRoof, customerSiteRestrictions } = useViewerStore();
    const [roofHeightSettings, setRoofHeightSettings] = useState({
        ...SLIDER_PROPERTIES_SETTINGS.ROOF_PROPERTIES.ROOF_HEIGHT.FOOT,
        min: convertQuantityToUnit(wallInitialHeight, METER, roofUnit),
    });
    useEffect(() => {
        const minHeight = convertQuantityToUnit(wallInitialHeight, METER, roofUnit);

        let maxHeight;
        switch (roofUnit) {
            case INCH:
                maxHeight = 960;
                break;
            case FOOT:
                maxHeight = 80;
                break;
            case METER:
                maxHeight = 24.384;
                break;
            default:
                maxHeight = 24.384;
                break;
        }

        const updatedSettings = {
            ...SLIDER_PROPERTIES_SETTINGS.ROOF_PROPERTIES.ROOF_HEIGHT[mappedUnit],
            min: minHeight,
            max: maxHeight,
        };

        setRoofHeightSettings(updatedSettings);
    }, [roofUnit, wallInitialHeight]);

    const roofHeightChangeHandler = (value: number) => {
        const minHeight = convertQuantityToUnit(wallInitialHeight, METER, roofUnit);
        const validValue = Math.max(value, minHeight);
        const roofHeightValueInMeter = convertQuantityToUnit(validValue, roofUnit, METER);
        useViewerStore.setState({wallEditedHeight: roofHeightValueInMeter});
        sceneViewer?.setWallSize(roofHeightValueInMeter-wallInitialHeight);
    };

    const roofHeightChangeCompleteHandler = (value: number) => {
        const originalValue = wallEditedHeight;
        roofHeightChangeHandler(value);
        const newValue = convertQuantityToUnit(value, roofUnit, METER);
        sceneViewer.addStep(EditAction.WallHeight, '', originalValue, newValue);
        sceneViewer.refreshShade()
    }

    const setMoveRoofMode = () => {
        useViewerStore.setState({isMoveRoofActive: !isMoveRoofActive});
        sceneViewer.setMoveMode(!isMoveRoofActive);
    }

    const getInputVal = (value: number) => {
        return Math.round(value * 100) / 100;
    }

    const clearModule = (selFace: RoofMesh) => {
        const {moduleCount, roofFaceId} = selFace;
        if (!moduleCount) return;
        sceneViewer.setClearFaceModule(selFace);
        const cloneFaceArr = [...selRoofFaceArr];
        cloneFaceArr.forEach(face => {
            if (face.roofFaceId === roofFaceId) face.moduleCount = 0;
        });
        useViewerStore.setState({selRoofFaceArr: cloneFaceArr});
    }


    const toggleRoofTag = () => {
        sceneViewer.toggleRoofTag(!isRoofTagVisible);
        useViewerStore.setState({isRoofTagVisible: !isRoofTagVisible});
    }

    useEffect(()=>{},[refreshQuestionableRoof]);

    const autoFillHandler = (direction: ModuleOrientation) => {
        sceneViewer.setRoofModuleDir(direction);
    }

    const showOtherReasonNotes = (item: RoofMesh) => {
        if(item.userData?.questionableRoof === 'Other (See Notes)' && item.userData?.questionableRoofNotes){
            return `Notes : ${item.userData?.questionableRoofNotes}`
        }
        return ''
    }

    return (
        user ?
            <div className='tab__inner'>
                <div className='tab__title'>
                    <span className='tab__title--text'>Roof Properties</span>
                </div>
                {hasCapability('Edit_Structure_Location') &&
                    <div className='tab__column'>
                        <Flex align='center' justify='center' gap={15}>
                            <Button
                                icon={<ArrowsOutCardinal size={18}/>}
                                className={`${isMoveRoofActive ? 'active' : ''}`}
                                type='default'
                                size='middle'
                                onClick={setMoveRoofMode}
                            >Move</Button>
                        </Flex>
                    </div>
                }

                {
                    hasCapability('Edit_Roof_Face') &&
                    <div id='wholeRoofInfo' className={`tab__column`}>
                        <span className="tab__column--subtitle">Edit Roof to Base Height</span>
                        <SliderInput
                            settings={roofHeightSettings}
                            value={GetRoundNum(convertTypeQuantityToUnit(ROOF as MeshType.Roof, wallEditedHeight, METER), 1)}
                            unit={renderUnit(roofUnit)}
                            onChange={roofHeightChangeHandler}
                            onChangeComplete={
                                (value) => roofHeightChangeCompleteHandler(value)
                            }
                        />
                    </div>
                }


                <div className='tab__column'>

                    <div className='tab__flex justify-between'>
                        <span className='tab__column--subtitle mb-0'>Show/Hide Roof Tag</span>
                        <Toggle
                            on={isRoofTagVisible}
                            onChange={toggleRoofTag}
                        />
                    </div>


                </div>

                <div className="tab__column">
                    <span className="tab__column--subtitle"><b>Customer and Site-Specific restrictions</b></span>
                    {(customerSiteRestrictions ?? '').trim() ? (
                        <p
                            className="tab__column--subtitle"
                            style={{ marginTop: 8, whiteSpace: 'pre-wrap', lineHeight: 1.5 }}
                        >
                            {customerSiteRestrictions}
                        </p>
                    ) : (
                        <p className="tab__column--subtitle" style={{ marginTop: 8, opacity: 0.65 }}>
                            None entered. Add or edit under Finalize.
                        </p>
                    )}
                </div>

                <div id='selectRoofInfo' className={`tab__column`} style={{display: 'none'}}>
                    {selRoofFaceArr.map((item, idx) => {
                            return (
                                <div className={`tab__column`} key={idx}>
                                    <span className="tab__column--subtitle">ID : Roof {item.userData.roof_tag_id}</span>
                                    <Divider/>
                                    <br/>
                                    <TabInput label='Azimuth' readOnly={true} align='input-flex' variant={'input-md'}
                                              initValue={getInputVal(item.oriAng.azimuth)} unit={'°'}/>
                                    <TabInput label='Pitch' readOnly={true} align='input-flex' variant={'input-md'}
                                              initValue={getInputVal(item.oriAng.tilt)} unit={'°'}/>
                                    <TabInput label='Size' readOnly={true} align='input-flex' variant={'input-md'}
                                              initValue={getInputVal(item.size)} unit={'sqFt'}/>

                                    <Divider/>

                                    <p>
                                        {refreshQuestionableRoof && <></>}
                                        <span className="tab__column--subtitle"><b>Questionable Roof : </b></span>
                                        <span className="tab__column--subtitle">
                                            {item.userData?.questionableRoof ? `Yes, Reason: ${item.userData?.questionableRoof} ${showOtherReasonNotes(item)}` : 'No'}
                                        </span>
                                    </p>

                                    <Divider/>

                                    <Accordion title='Solar Layout Options'>
                                    <Flex vertical
                                        gap={15}
                                        align={'center'}
                                        justify={'center'}
                                        style={{paddingTop: '10px'}}
                                    >
                                        {
                                            hasCapability('Edit_Module_Remove') &&
                                            <Button
                                                icon={<Eraser size={20}/>}
                                                className={"full-width"}
                                                id={'btnDeleteAllModules'} size="middle"
                                                onClick={() => clearModule(item)}>Clear Layout</Button>
                                        }

                                        {
                                            (hasCapability('Mark_Questionable_Roof') || hasCapability('Mark Questionable Roof')) &&
                                            <Button
                                                icon={<Question size={20}/>}
                                                className={"full-width"}
                                                id={'btnQuestionableRoof'} size="middle"
                                                onClick={() => sceneViewer.setQuestionable(item)}>Set Questionable
                                                Roof</Button>
                                        }

                                        {
                                            (hasCapability('Mark_Questionable_Roof') || hasCapability('Mark Questionable Roof')) &&
                                            <Button
                                                icon={<XSquare size={20}/>}
                                                className={"full-width"}
                                                id={'btnQuestionableRoof'} size="middle"
                                                onClick={() => sceneViewer.unsetQuestionable(item)}>Unset Questionable
                                                Roof</Button>
                                        }

                                        {
                                            <Button
                                                disabled={item.userData?.pitchOverride===0}
                                                icon={<MdOutlineCropPortrait size={20}/>}
                                                className={"full-width"}
                                                size="middle"
                                                onClick={() => autoFillHandler(ModuleOrientation.Portrait)}>
                                                AutoFill Portrait
                                            </Button>
                                        }

                                        {
                                            <Button
                                                icon={<MdOutlineCropLandscape size={20}/>}
                                                className={"full-width"}
                                                size="middle"
                                                onClick={() => autoFillHandler(ModuleOrientation.Landscape)}
                                            >
                                                AutoFill Landscape
                                            </Button>
                                        }

                                        {
                                            <Button
                                                disabled={item.userData?.pitchOverride===0}
                                                icon={<BsGrid1X2 size={20}/>}
                                                className={"full-width"}
                                                size="middle"
                                                onClick={() => autoFillHandler(ModuleOrientation.Both)}>
                                                AutoFill Both
                                            </Button>
                                        }

                                        <Button
                                            icon={<RiBuildingFill size={20}/>}
                                            className={"full-width"}
                                            id={'btnQuestionableRoof'} size="middle"
                                            onClick={() => setBuildingAsDetachedStructure(true, item)}>Mark as Detached Structure
                                        </Button>

                                        <Button
                                            icon={<XSquare size={20}/>}
                                            className={"full-width"}
                                            id={'btnQuestionableRoof'} size="middle"
                                            onClick={() => setBuildingAsDetachedStructure(false,item)}>Unmark as Detached Structure
                                        </Button>


                                    </Flex>
                                    </Accordion>

                                </div>)
                        }
                    )}
                </div>

            </div> : <></>
    );
}

export default RoofProperties;